import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Redirect } from "react-router-dom";
import firebase from "services/firebase/firebase.config";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useAuthStore } from "stores/authStore/authStore";
import { ColorV2 } from "config/colors";
import { InputField } from "components/designV2/inputfield/inputfield";
import type { BaseButtonStyle } from "components/designV2/buttons/baseButton";
import { CompanyRole } from "model/User";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import {
  ErrorText,
  FieldsWrapper,
  FormStyled,
  PageActions,
} from "./loginForm.styled";

const LOGIN_PRIMARY_BUTTON_STYLES: Partial<BaseButtonStyle> = {
  backgroundColor: ColorV2.PositiveGreen,
  hoverBackgroundColor: ColorV2.SimflexityGreen,
  activeBackgroundColor: ColorV2.SimflexityGreen,
  disabledBackgroundColor: ColorV2.PositiveGreenLight,
  disabledTextColor: ColorV2.White,
  disabledBorderColor: "transparent",
};

type Props = {
  email: string;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
};

export const LoginForm: React.FC<Props> = ({ email, setEmail }) => {
  const { t } = useTranslation();
  const [authState, , authDispatch] = useAuthStore();
  const [companyState] = useCompanyStore();
  const [password, setPassword] = useState("");
  const trimmedEmail = email.trim();
  const canLogIn = Boolean(trimmedEmail && password);

  useEffect(() => {
    authDispatch({ type: "SET_LOGIN_ERROR", payload: null });

    return () => {
      authDispatch({ type: "SET_LOGIN_ERROR", payload: null });
    };
  }, [authDispatch, email, password]);

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (canLogIn) {
      authDispatch({ type: "SET_IS_LOADING", payload: true });
      try {
        if (firebase.auth().currentUser) {
          await firebase.auth().signOut();
        }

        await firebase
          .auth()
          .signInWithEmailAndPassword(trimmedEmail, password);
      } catch {
        authDispatch({ type: "SET_LOGIN_ERROR", payload: "credentials" });
        authDispatch({ type: "SET_IS_LOADING", payload: false });
      }
    }
  };

  return (
    <>
      {authState.signedIn && companyState.companies.length > 0 && (
        <Redirect
          to={
            companyState.companies.length > 1
              ? "./company-select"
              : authState.user?.role === CompanyRole.COMPANY_TIMESTAMP
                ? "./stampclock"
                : "./schedule"
          }
        />
      )}
      <FormStyled onSubmit={handleLogin}>
        <FieldsWrapper>
          <InputField
            label={t("LoginCompany.Email")}
            type="email"
            value={email}
            placeholder={t("LoginCompany.Email")}
            fullWidth
            onChange={setEmail}
            onClear={() => setEmail("")}
            disabled={authState.isLoading}
          />
          <InputField
            label={t("LoginCompany.Password")}
            type="password"
            value={password}
            placeholder={t("LoginCompany.Password")}
            fullWidth
            onChange={setPassword}
            onClear={() => setPassword("")}
            disabled={authState.isLoading}
          />
        </FieldsWrapper>
        {authState.loginError && (
          <ErrorText>
            {authState.loginError === "credentials"
              ? t("LoginCompany.EmailPasswordCombination")
              : t("LoginCompany.CouldNotLogin")}
          </ErrorText>
        )}
        <PageActions>
          <PrimaryButton
            type="submit"
            label={t("LoginCompany.Login")}
            disabled={!canLogIn}
            isLoading={authState.isLoading}
            styleOverrides={LOGIN_PRIMARY_BUTTON_STYLES}
          />
        </PageActions>
      </FormStyled>
    </>
  );
};
